export type CronFrequency = 'every_n_minutes' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'custom';

export interface CronBuilderState {
  frequency: CronFrequency;
  intervalMinutes: number;
  minute: number;
  hour: number;
  weekdays: number[];
  dayOfMonth: number;
  customExpression: string;
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function isIntegerInRange(value: number, min: number, max: number): boolean {
  return Number.isInteger(value) && value >= min && value <= max;
}

function isValidCronPart(part: string, min: number, max: number): boolean {
  if (part === '*') {
    return true;
  }

  if (part.startsWith('*/')) {
    const step = Number(part.slice(2));
    return isIntegerInRange(step, 1, max);
  }

  return part.split(',').every((token) => {
    const [range, stepRaw] = token.split('/');
    const step = stepRaw === undefined ? 1 : Number(stepRaw);
    if (!isIntegerInRange(step, 1, max)) {
      return false;
    }

    if (range === '*') {
      return true;
    }

    if (range.includes('-')) {
      const [startRaw, endRaw] = range.split('-');
      const start = Number(startRaw);
      const end = Number(endRaw);
      return isIntegerInRange(start, min, max) && isIntegerInRange(end, min, max) && start <= end;
    }

    return isIntegerInRange(Number(range), min, max);
  });
}

export function isValidCronExpression(expression: string): boolean {
  const fields = expression.trim().split(/\s+/);
  if (fields.length !== 5) {
    return false;
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = fields;
  return (
    isValidCronPart(minute, 0, 59) &&
    isValidCronPart(hour, 0, 23) &&
    isValidCronPart(dayOfMonth, 1, 31) &&
    isValidCronPart(month, 1, 12) &&
    isValidCronPart(dayOfWeek, 0, 7)
  );
}

function parseNumberField(value: string): number | null {
  if (!/^\d+$/.test(value)) {
    return null;
  }
  return Number(value);
}

function parseWeekdays(value: string): number[] | null {
  if (value === '*') {
    return [];
  }
  const days = value.split(',').map((token) => Number(token));
  if (days.some((day) => !isIntegerInRange(day, 0, 7))) {
    return null;
  }
  return Array.from(new Set(days.map((day) => (day === 7 ? 0 : day)))).sort((a, b) => a - b);
}

export function createDefaultCronState(expression = '0 1 * * *'): CronBuilderState {
  return parseCronExpression(expression);
}

export function parseCronExpression(expression: string): CronBuilderState {
  const trimmed = expression.trim();
  const fallback: CronBuilderState = {
    frequency: 'custom',
    intervalMinutes: 5,
    minute: 0,
    hour: 1,
    weekdays: [1],
    dayOfMonth: 1,
    customExpression: trimmed,
  };

  const fields = trimmed.split(/\s+/);
  if (fields.length !== 5 || !isValidCronExpression(trimmed)) {
    return fallback;
  }

  const [minuteField, hourField, dayField, monthField, weekdayField] = fields;

  if (/^\*\/\d+$/.test(minuteField) && hourField === '*' && dayField === '*' && monthField === '*' && weekdayField === '*') {
    return {
      ...fallback,
      frequency: 'every_n_minutes',
      intervalMinutes: Number(minuteField.slice(2)),
      customExpression: trimmed,
    };
  }

  const minute = parseNumberField(minuteField);
  if (minute === null) {
    return fallback;
  }

  if (hourField === '*' && dayField === '*' && monthField === '*' && weekdayField === '*') {
    return {
      ...fallback,
      frequency: 'hourly',
      minute,
      customExpression: trimmed,
    };
  }

  const hour = parseNumberField(hourField);
  if (hour === null || monthField !== '*') {
    return fallback;
  }

  if (dayField === '*' && weekdayField === '*') {
    return {
      ...fallback,
      frequency: 'daily',
      minute,
      hour,
      customExpression: trimmed,
    };
  }

  if (dayField === '*' && weekdayField !== '*') {
    const weekdays = parseWeekdays(weekdayField);
    if (!weekdays || weekdays.length === 0) {
      return fallback;
    }
    return {
      ...fallback,
      frequency: 'weekly',
      minute,
      hour,
      weekdays,
      customExpression: trimmed,
    };
  }

  const dayOfMonth = parseNumberField(dayField);
  if (dayOfMonth !== null && weekdayField === '*') {
    return {
      ...fallback,
      frequency: 'monthly',
      minute,
      hour,
      dayOfMonth,
      customExpression: trimmed,
    };
  }

  return fallback;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function formatTime(hour: number, minute: number): string {
  return `${pad(hour)}:${pad(minute)}`;
}

export function buildCronExpression(state: CronBuilderState): string {
  switch (state.frequency) {
    case 'every_n_minutes':
      return `*/${state.intervalMinutes} * * * *`;
    case 'hourly':
      return `${state.minute} * * * *`;
    case 'daily':
      return `${state.minute} ${state.hour} * * *`;
    case 'weekly': {
      const days = (state.weekdays.length > 0 ? state.weekdays : [1]).join(',');
      return `${state.minute} ${state.hour} * * ${days}`;
    }
    case 'monthly':
      return `${state.minute} ${state.hour} ${state.dayOfMonth} * *`;
    case 'custom':
      return state.customExpression.trim();
    default:
      return state.customExpression.trim();
  }
}

export function humanizeCronExpression(expression: string): string {
  const state = parseCronExpression(expression);
  switch (state.frequency) {
    case 'every_n_minutes':
      return state.intervalMinutes === 1
        ? 'Every minute (UTC)'
        : `Every ${state.intervalMinutes} minutes (UTC)`;
    case 'hourly':
      return state.minute === 0
        ? 'Hourly (UTC)'
        : `Hourly at :${pad(state.minute)} UTC`;
    case 'daily':
      return `Daily at ${formatTime(state.hour, state.minute)} UTC`;
    case 'weekly': {
      const days = state.weekdays.map((day) => WEEKDAY_LABELS[day] ?? String(day)).join(', ');
      return `Weekly on ${days} at ${formatTime(state.hour, state.minute)} UTC`;
    }
    case 'monthly':
      return `Monthly on day ${state.dayOfMonth} at ${formatTime(state.hour, state.minute)} UTC`;
    default:
      return `${expression.trim() || 'Invalid cron'} (UTC)`;
  }
}

function copyUtc(from: Date): Date {
  return new Date(from.getTime());
}

export function getNextRunAt(expression: string, from: Date = new Date()): Date | null {
  const state = parseCronExpression(expression);
  if (state.frequency === 'custom' || !isValidCronExpression(buildCronExpression(state))) {
    return null;
  }

  const cursor = copyUtc(from);
  cursor.setUTCSeconds(0, 0);

  if (state.frequency === 'every_n_minutes') {
    cursor.setUTCMilliseconds(0);
    for (let i = 0; i < 24 * 60; i += 1) {
      cursor.setUTCMinutes(cursor.getUTCMinutes() + 1);
      if (cursor.getUTCMinutes() % state.intervalMinutes === 0) {
        return new Date(cursor.getTime());
      }
    }
    return null;
  }

  if (state.frequency === 'hourly') {
    const next = copyUtc(cursor);
    next.setUTCMinutes(state.minute, 0, 0);
    if (next <= from) {
      next.setUTCHours(next.getUTCHours() + 1);
    }
    return next;
  }

  if (state.frequency === 'daily') {
    const next = copyUtc(cursor);
    next.setUTCHours(state.hour, state.minute, 0, 0);
    if (next <= from) {
      next.setUTCDate(next.getUTCDate() + 1);
    }
    return next;
  }

  if (state.frequency === 'weekly') {
    const days = state.weekdays.length > 0 ? state.weekdays : [1];
    for (let offset = 0; offset < 8; offset += 1) {
      const next = copyUtc(cursor);
      next.setUTCDate(next.getUTCDate() + offset);
      next.setUTCHours(state.hour, state.minute, 0, 0);
      if (days.includes(next.getUTCDay()) && next > from) {
        return next;
      }
    }
    return null;
  }

  if (state.frequency === 'monthly') {
    for (let offset = 0; offset < 24; offset += 1) {
      const next = copyUtc(cursor);
      next.setUTCDate(1);
      next.setUTCMonth(next.getUTCMonth() + offset);
      const lastDay = new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 0)).getUTCDate();
      if (state.dayOfMonth > lastDay) {
        continue;
      }
      next.setUTCDate(state.dayOfMonth);
      next.setUTCHours(state.hour, state.minute, 0, 0);
      if (next > from) {
        return next;
      }
    }
  }

  return null;
}

export function formatNextRun(date: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${date.getUTCDate()} ${months[date.getUTCMonth()]} ${date.getUTCFullYear()}, ${formatTime(date.getUTCHours(), date.getUTCMinutes())} UTC`;
}

export const WEEKDAY_OPTIONS = WEEKDAY_LABELS.map((label, value) => ({ label, value }));
